/**
 * Network calls behind Create Schedule.
 *
 * Three endpoints are involved, and they do not share credentials:
 *
 *   /dashboard-details/:id   ours. Returns the report with its resource
 *                            documents inlined, which is where the scheduler's
 *                            BaseURL / APIRoute / APIReqParams come from — the
 *                            plain /dashboard list does not inline them.
 *                            Authenticated with the app's own accessToken.
 *
 *   /tokenservice            Witmeg token service. The scheduler will not take
 *                            the app's accessToken, so a service token is
 *                            minted for it first. Ported from the RewardsTick
 *                            manager app, which does the same thing.
 *
 *   /v1/scheduler/create     the report scheduler, a separate service. Takes
 *                            the service token above, not our accessToken.
 */

import Axios from "axios";

const { REACT_APP_CONNECTION_INFO_BASE_URL, REACT_APP_SCHEDULER_BASE_URL, REACT_APP_TOKEN_SERVICE_URL } = process.env;

/* Posting is off until someone has decided it is safe to create live schedules
   in the dev scheduler — a created schedule really does send mail when it
   fires. With it off, submit() still builds the payload and logs it, so the
   mapping can be checked against the spec without side effects.

   Everything below is complete: flipping this to true is the only change
   needed. */
export const SCHEDULER_POST_ENABLED = true;

/* Backend's instruction: the scheduler's create endpoint is registered under
   this service name. */
const SCHEDULER_SERVICE_NAME = "scheduler";

const appAuthHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
});

/* Shown when the failure has nothing a user could act on — a 405, an empty
   body, a JWT complaint from the gateway. The technical detail still goes to
   the console; it just does not belong on screen. */
export const GENERIC_ERROR = "Something went wrong. Please try again.";

/**
 * One error for the console and one for the user.
 *
 * `technical` is logged and kept on the error as `.technical`; `friendly`
 * becomes the message callers display. Anything the scheduler itself wrote
 * (its `Result` text) is worth showing; anything from the gateway or the
 * transport is not.
 */
function schedulerError(technical, friendly) {
  // eslint-disable-next-line no-console
  console.error("[scheduler]", technical);
  const error = new Error(friendly || GENERIC_ERROR);
  error.technical = technical;
  /* Only a message we deliberately wrote for a user is safe to display.
     Everything else — a fetch TypeError, an axios "Network Error", a plain bug
     — reads as gibberish on screen. */
  error.userFacing = Boolean(friendly);
  return error;
}

/**
 * What to put in a toast: the scheduler's own words when it gave any, otherwise
 * wording that at least names the action.
 *
 * Deliberately an allow-list rather than a deny-list. Filtering out known-bad
 * strings meant "Failed to fetch" and "Network Error" leaked through, because
 * there is no end to the messages a browser or a library can invent.
 */
export const actionError = (error, fallback) => {
  if (error?.userFacing && error.message) return error.message;
  // Unmarked errors never reached schedulerError, so log them here instead.
  if (error && !error.technical) {
    // eslint-disable-next-line no-console
    console.error("[scheduler]", error);
  }
  return fallback || GENERIC_ERROR;
};

function requireEnv(value, name) {
  if (!value) {
    // Developer guidance, so it goes to the console; the user sees the generic.
    throw schedulerError(
      `${name} is not set. If it was just added to .env, restart the dev server — Create React App only reads env files at start-up.`,
    );
  }
  return value;
}

/**
 * Mint a service token for one of the Witmeg backend services.
 *
 * Same shape as the RewardsTick manager's generateToken — their
 * getLoggedUserDetails().AccessToken is our localStorage "accessToken", set at
 * login from the same userResponse.AccessToken field.
 */
export async function generateToken(serviceName = "default", isLoginServerRequest = false) {
  /* CRA inlines env vars at start-up, so a dev server started before these
     were added to .env sees them as undefined — and axios then dies on
     "Cannot read properties of undefined (reading 'protocol')", which says
     nothing about the actual cause. Check it here instead. */
  requireEnv(REACT_APP_TOKEN_SERVICE_URL, "REACT_APP_TOKEN_SERVICE_URL");

  const accessToken = localStorage.getItem("accessToken");

  const data = {
    serviceName,
    isLoginServerRequest,
    ...(accessToken && { usertoken: accessToken }),
  };

  const response = await Axios.post(REACT_APP_TOKEN_SERVICE_URL, data);
  const token = response?.data?.token;
  if (!token) {
    throw schedulerError(`Token service returned no token for "${serviceName}"`);
  }
  return token;
}

/**
 * The selected report, with its resources resolved. Needed before the payload
 * can carry its API fields.
 */
export async function fetchReportBoard(reportId) {
  const response = await fetch(`${REACT_APP_CONNECTION_INFO_BASE_URL}/dashboard-details/${reportId}`, {
    headers: appAuthHeaders(),
  });
  if (!response.ok) {
    throw schedulerError(`Could not load report ${reportId} (${response.status})`, "Could not load the selected report.");
  }
  const body = await response.json();
  if (!body?.success || !body?.data) {
    throw schedulerError(`Report ${reportId} came back empty`, "Could not load the selected report.");
  }
  return body.data;
}

/**
 * Timezones the scheduler recognises — GET /v1/scheduler/countries/timezone.
 *
 * The response shape isn't documented, so the result is normalised into
 * { value, label } pairs and anything unrecognised is skipped rather than
 * rendered as "[object Object]". `value` is what goes in the payload's
 * TimeZone field; `label` is only for the select.
 */
export async function fetchTimeZones() {
  requireEnv(REACT_APP_SCHEDULER_BASE_URL, "REACT_APP_SCHEDULER_BASE_URL");

  const token = await generateToken(SCHEDULER_SERVICE_NAME);
  const response = await fetch(`${REACT_APP_SCHEDULER_BASE_URL}/v1/scheduler/countries/timezone`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    throw new Error(`Could not load timezones (${response.status})`);
  }
  return normalizeTimeZones(await response.json());
}

/* The live response is:
     { "Status": true, "Result": [ { "Country": "SriLanka",
                                     "Region": "Asia/Colombo",
                                     "TimeInterval": "",
                                     "IsActive": true }, ... ] }

   `Region` is the IANA id and the value the payload's TimeZone field wants;
   `Country` is only a label. Region is checked first for that reason — the
   other keys are kept as a cushion in case the shape shifts. */
export function normalizeTimeZones(body) {
  const list = [body?.Result, body?.data, body?.result, body?.timezones, body].find(Array.isArray) || [];

  const ID_KEYS = ["Region", "TimeZone", "Timezone", "ZoneName", "Zone", "Value", "Name"];
  const LABEL_KEYS = ["Country", "CountryName", "DisplayName", "Label", "Description"];

  return list
    .map((entry) => {
      if (typeof entry === "string") return { value: entry, label: entry };
      if (!entry || typeof entry !== "object") return null;
      // Zones the scheduler has switched off should not be offerable.
      if (entry.IsActive === false) return null;

      const value = ID_KEYS.map((k) => entry[k]).find((v) => typeof v === "string" && v.trim());
      if (!value) return null;

      const extra = LABEL_KEYS.map((k) => entry[k]).find((v) => typeof v === "string" && v.trim());
      return { value, label: extra && extra !== value ? `${value} (${extra})` : value };
    })
    .filter(Boolean);
}

/**
 * Every scheduler call is the same shape: mint a token, POST JSON, unwrap the
 * { Status, Result } envelope.
 *
 * All four live methods are POST — including edit, which the doc lists as PUT.
 * PUT returns 404 "Method does not exist" while POST returns 401, so the doc is
 * wrong on the method.
 */
async function schedulerRequest(path, body, method = "POST") {
  requireEnv(REACT_APP_SCHEDULER_BASE_URL, "REACT_APP_SCHEDULER_BASE_URL");

  const token = await generateToken(SCHEDULER_SERVICE_NAME);

  let response;
  try {
    response = await fetch(`${REACT_APP_SCHEDULER_BASE_URL}${path}`, {
      method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
  } catch (cause) {
    /* fetch only throws for transport failures, and a CORS preflight the
       server refuses looks identical to the network being down: TypeError
       "Failed to fetch", no response to inspect. That is the likely shape of a
       method the gateway does not allow, so it is flagged for the retry in
       updateSchedule rather than reported as a dead end. */
    const error = schedulerError(`${method} ${path} could not be reached: ${cause?.message || cause}`);
    error.transport = true;
    throw error;
  }

  const text = await response.text();
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch {
    parsed = null;
  }

  if (!response.ok) {
    /* Cloud Endpoints rejects at two stages: detail "service_control" means the
       path is not registered for this method, "auth" means it is but the token
       was refused. None of it means anything to a user, so it is logged rather
       than shown. */
    const stage = parsed?.details?.[0]?.detail;
    const detail = parsed?.message || text || "no response body";
    const error = schedulerError(
      `${method} ${path} failed (${response.status})${stage ? ` [${stage}]` : ""}: ${detail}`,
    );
    /* 405 and an unregistered-route 404 both mean "wrong method for this path"
       rather than "this failed" — updateSchedule retries with POST on either. */
    error.unroutable = response.status === 405 || (response.status === 404 && stage === "service_control");
    throw error;
  }

  /* 200 does not mean success here — the scheduler answers 200 and puts the
     outcome in the envelope, so "Error while inserting scheduler info" arrives
     as a 200. Checking response.ok alone would report failures as successes.

     Result is the scheduler's own words, so it is worth showing; when it is
     absent there is nothing to say beyond the generic message. */
  if (parsed?.Status === false) {
    const reason = typeof parsed.Result === "string" ? parsed.Result : "";
    throw schedulerError(`${method} ${path} returned Status:false — ${reason || "no Result"}`, reason);
  }

  return parsed;
}

/** POST /v1/scheduler/all — every schedule for one organization. */
export async function fetchSchedules(organizationID) {
  const body = await schedulerRequest("/v1/scheduler/all", { OrganizationID: organizationID });
  const list = Array.isArray(body?.Result) ? body.Result : [];
  /* Soft-deleted rows stay in the collection, so filter them out here. Kept
     even though the service is not persisting IsDeleted today — it costs
     nothing and starts working the moment the service honours the flag.

     Note this cannot also filter on IsActive === false, which is what delete
     now sets: saving a schedule as a draft sets IsActive false too, and
     IsDraft is never returned, so there is no way to tell the two apart. Until
     the service gives back a real deleted flag, a deleted schedule stays in
     the list showing as "Draft" — see softDeleteSchedule. */
  // return list.filter((s) => !s.IsDeleted);
  /* Only active schedules, as decided 2026-10-13: delete is a soft delete that
     sets IsActive false (see softDeleteSchedule), so this is what keeps deleted
     schedules out of the list. Status is left alone — the scheduler only accepts
     Draft and Published, so there is no "Deleted" value to mark them with.

     Known trade-off: Keep as Draft also saves IsActive false (see
     buildSchedulePayload), so drafts are hidden by this too. Revisit when the
     service persists IsDeleted (or gives a deleted marker) — then filter on that
     and drafts can come back. */
  // return list.filter((s) => !s.IsDeleted && s.IsActive === true);
  /* IsActive filter removed (2026-10-13) — it hid every draft too. Delete now
     sends IsDeleted: true (see the experiment in softDeleteSchedule), so this
     is the only filter. If the service turns out not to persist IsDeleted,
     deleted schedules will show again (as Draft) until it does. */
  // return list.filter((s) => !s.IsDeleted);

  /* ── IsActive MEANS "NOT DELETED" (2026-10-13) ──
     The experiment settled it: the service does not persist IsDeleted on /edit,
     so it cannot mark a deletion. IsActive now does that job alone — delete sets
     it false, and Keep as Draft no longer does (see buildSchedulePayload), so
     drafts survive this filter. Draft vs Published is told apart by Status.

     Drafts saved before this change still carry IsActive false and stay hidden;
     re-saving one is not possible from the list, so they are effectively gone. */
  return list.filter((s) => !s.IsDeleted && s.IsActive === true);
}

/** POST /v1/scheduler/id — one schedule. */
export async function fetchScheduleById(id) {
  const body = await schedulerRequest("/v1/scheduler/id", { ID: id });
  return body?.Result || null;
}

/**
 * How many runs to ask for — and the whole of the paging story.
 *
 * The endpoint takes Page and PageSize but returns no total count, and the scheduler team
 * has said that is deliberate: they intend to serve only recent logs and keep old ones out
 * of the query, to spare the database. 60 on page 0 is the size they asked us to use.
 *
 * So there is no second page to fetch and no pager to build. If a schedule has run more
 * than 60 times, the older runs are simply not available here.
 */
export const LOG_PAGE_SIZE = 60;

/**
 * POST /v1/scheduler/task/logs — what each run of a schedule actually did.
 *
 * One entry per run, each carrying a UserLogs array of per-recipient deliveries. This is
 * the first real data the Schedule Logs page has had; it ran on a mock file before, which
 * showed every schedule the same invented history.
 *
 * `scheduleID` is optional in the API. Passed, it narrows to one schedule — which is what
 * the page reached from the list's log icon wants. Omitted, it returns the organisation's
 * whole history.
 */
export async function fetchScheduleLogs({ organizationID, scheduleID = "", page = 0, pageSize = LOG_PAGE_SIZE }) {
  const body = await schedulerRequest("/v1/scheduler/task/logs", {
    OrganizationID: organizationID,
    Page: page,
    PageSize: pageSize,
    ScheduleID: scheduleID,
  });

  return Array.isArray(body?.Result) ? body.Result : [];
}

/**
 * /v1/scheduler/edit — PUT per the API doc.
 *
 * The dev service has not caught up: as of 2026-09-02, PUT and PATCH both
 * answer 404 "Method does not exist" (from the route table, before auth) while
 * POST answers 401. So the documented method is tried first and POST is the
 * fallback — that way this follows the doc, works against what is deployed
 * today, and switches over on its own once PUT is registered.
 *
 * Drop the fallback once PUT works; the warning below is the signal.
 */
export async function updateSchedule(payload) {
  if (!payload?.ID) {
    throw schedulerError("updateSchedule called without an ID.");
  }

  try {
    return await schedulerRequest("/v1/scheduler/edit", payload, "PUT");
  } catch (error) {
    /* Retry as POST for anything that means "not this method here": a 405, an
       unregistered-route 404, or a transport failure — which is what a refused
       CORS preflight looks like from the browser. Genuine rejections (401, a
       validation error, Status:false) fall through untouched. */
    if (!error.unroutable && !error.transport) throw error;
    // eslint-disable-next-line no-console
    console.warn("PUT /v1/scheduler/edit did not go through — retrying as POST.", error.technical || error);
    return schedulerRequest("/v1/scheduler/edit", payload, "POST");
  }
}

/**
 * Delete, as a soft delete through edit — /v1/scheduler/delete answers "Method
 * does not exist" under every method, so the removal has to be a flag on the
 * record.
 *
 * IsDeleted was the obvious flag and the model carries it, but sending it had
 * no effect: the record came back from /id with IsDeleted still false, so the
 * service is not persisting it. IsActive is used instead — it is a field the
 * service demonstrably round-trips (it is what the list reads for its status),
 * and switching it off is what actually stops the schedule delivering.
 *
 * Reads the schedule by id first rather than reusing the row from the list:
 * /v1/scheduler/all returns a partial record, /v1/scheduler/id returns the
 * whole thing, and edit validates the Required fields — so the list row on its
 * own would fail validation.
 */
export async function softDeleteSchedule(idOrRecord) {
  const id = typeof idOrRecord === "string" ? idOrRecord : idOrRecord?.ID || idOrRecord?.ScheduleID;
  if (!id) {
    throw schedulerError("softDeleteSchedule called without an ID.");
  }

  const full = await fetchScheduleById(id);
  if (!full) {
    throw schedulerError(
      `Schedule ${id} could not be read back before deleting.`,
      "This schedule could not be deleted. Please try again.",
    );
  }

  // return updateSchedule({ ...full, ID: id, IsActive: false });

  /* ── EXPERIMENT (2026-10-13): does the service persist IsDeleted now? ──
     IsDeleted: true is sent alongside IsActive: false — IsActive stays so the
     delete still works whichever way this turns out. The record is then read
     back and both flags logged, sent vs stored. If IsDeleted comes back true,
     the list can filter on it (fetchSchedules already drops IsDeleted rows) and
     drafts can come back by dropping the IsActive filter there.

     Remove the read-back and the logging once the answer is known. */
  // const result = await updateSchedule({ ...full, ID: id, IsActive: false, IsDeleted: true });
  //
  // try {
  //   const after = await fetchScheduleById(id);
  //   // eslint-disable-next-line no-console
  //   console.log("[softDelete experiment]", {
  //     id,
  //     sent: { IsDeleted: true, IsActive: false },
  //     stored: { IsDeleted: after?.IsDeleted, IsActive: after?.IsActive },
  //     isDeletedPersisted: after?.IsDeleted === true,
  //   });
  // } catch (error) {
  //   // The delete itself went through; only the check failed.
  //   // eslint-disable-next-line no-console
  //   console.warn("[softDelete experiment] could not read the schedule back", error);
  // }
  //
  // return result;

  /* Result of the experiment above: IsDeleted came back false — the service
     ignores it on /edit. IsActive: false is the deletion marker now (the list
     shows only IsActive true; drafts keep IsActive true). IsDeleted: true is
     still sent: it costs nothing and is right the day the service honours it. */
  return updateSchedule({ ...full, ID: id, IsActive: false, IsDeleted: true });
}

/**
 * Hand a built payload to the scheduler. Inert while SCHEDULER_POST_ENABLED is
 * false — it resolves with the payload instead of sending, so callers can be
 * written as though it were live.
 */
export async function createSchedule(payload) {
  if (!SCHEDULER_POST_ENABLED) {
    return { skipped: true, payload };
  }

  requireEnv(REACT_APP_SCHEDULER_BASE_URL, "REACT_APP_SCHEDULER_BASE_URL");

  // Fetched per call rather than cached: one create is one token, and a stale
  // cached token is a worse failure than an extra round trip.
  const token = await generateToken(SCHEDULER_SERVICE_NAME);

  const response = await fetch(`${REACT_APP_SCHEDULER_BASE_URL}/v1/scheduler/create`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  const text = await response.text();
  let body;
  try {
    body = JSON.parse(text);
  } catch {
    body = null;
  }

  if (!response.ok) {
    throw new Error(`Scheduler rejected the schedule (${response.status}) ${text}`.trim());
  }

  /* The scheduler answers 200 even when it failed, putting the outcome in the
     envelope instead — "Error while inserting scheduler info" and friends
     arrive as 200 + Status:false. Checking response.ok alone would report those
     as saved and navigate away from a schedule that does not exist. */
  if (body?.Status === false) {
    throw new Error(body.Result || "The scheduler could not create the schedule.");
  }

  /* Status:true with an all-zero ObjectId is also a failure — confirmed by
     /v1/scheduler/all not returning the schedule afterwards. Treated as an
     error so it cannot pass for success either. */
  const id = typeof body?.Result === "string" ? body.Result : "";
  if (/^0+$/.test(id)) {
    throw schedulerError(
      "Create returned Status:true with an all-zero ObjectId — the schedule was not persisted.",
      "The schedule could not be saved. Please try again.",
    );
  }

  return body;
}
